import React, { useState, useEffect, useCallback, useRef } from "react";
import { usePageTitle } from "../../hooks/usePageTitle";
import { useAuth } from "../../context/AuthContext";
import CohortFilterForm, { CohortFilterCriteria } from "./CohortFilterForm";
import SellersResultTable from "./SellersResultTable";
import CohortReviewPage from "./CohortReviewPage";
import { AddressValidationPage } from "./AddressValidationPage";
import { PpcFarmSellersMasterDocumentClient, AddressValidationResult } from "../../types/ppcFarmTypes";
import { addressValidationService } from "../../services/addressValidationService";
import { cohortReviewService } from "../../services/cohortReviewService";
import { getFunctions, httpsCallable } from "firebase/functions";

// Seller with document ID for display
interface SellerWithId extends PpcFarmSellersMasterDocumentClient {
  id: string;
}

// API response interface
interface FilterResponse {
  totalCount: number;
  sellers: SellerWithId[];
  lastVisible?: string;
}

const PpcFarmCohortBuilderClient: React.FC = () => {
  usePageTitle("Cohort Builder | PPC Farm Mail Tracker");
  const { userEmail } = useAuth();

  // View state management
  const [currentView, setCurrentView] = useState<'filters' | 'review' | 'validation'>('filters');

  // Filter state
  const [filters, setFilters] = useState<CohortFilterCriteria>({
    minProducts: "",
    maxProducts: "",
    minRatings: "",
    maxRatings: "",
    minBrands: "",
    maxBrands: "",
    country: "US", // Default to US
    excludeFromTargetLedger: true, // Changed from false to true - default ON
    // New filters
    queryable_hasFBA: "",
    minRating: "",
    maxRating: "",
    excludeSellersFromChina: true, // Changed from false to true - default ON
    minAvgBuyBoxCompetitors: "",
    maxAvgBuyBoxCompetitors: "",
    minBuyBoxNewOwnershipRate: "",
    maxBuyBoxNewOwnershipRate: "",
    minBuyBoxUsedOwnershipRate: "",
    maxBuyBoxUsedOwnershipRate: "",
  });

  // Results state
  const [totalCount, setTotalCount] = useState<number>(0);
  const [allSellers, setAllSellers] = useState<SellerWithId[]>([]); // Original filtered results
  const [reviewSellers, setReviewSellers] = useState<SellerWithId[]>([]); // Sellers after deletions
  const [deletedSellerIds, setDeletedSellerIds] = useState<Set<string>>(new Set());
  const [lastVisible, setLastVisible] = useState<string | undefined>();
  const [currentOffset, setCurrentOffset] = useState<number>(0);
  const [hasMore, setHasMore] = useState(false);

  // Loading states
  const [sellersLoading, setSellersLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [countLoading, setCountLoading] = useState(false);

  // Error state
  const [error, setError] = useState<string | null>(null);

  // Address validation state
  const [validationResults, setValidationResults] = useState<AddressValidationResult[]>([]);
  const [isValidationInProgress, setIsValidationInProgress] = useState(false);

  // Add loading ref to prevent multiple simultaneous calls
  const isLoadingRef = useRef(false);

  // API call function with robust error handling and timeout - now using Firebase Functions
  const callFilterAPI = async (
    criteria: CohortFilterCriteria,
    countOnly: boolean = false,
    cursor?: string
  ): Promise<FilterResponse> => {
    const user = await import("../../config/firebase").then(m => m.auth.currentUser);
    if (!user) throw new Error("User not authenticated");

    const requestData = {
      minProducts: criteria.minProducts ? parseInt(criteria.minProducts) : undefined,
      maxProducts: criteria.maxProducts ? parseInt(criteria.maxProducts) : undefined,
      minRatings: criteria.minRatings ? parseInt(criteria.minRatings) : undefined,
      maxRatings: criteria.maxRatings ? parseInt(criteria.maxRatings) : undefined,
      minBrands: criteria.minBrands ? parseInt(criteria.minBrands) : undefined,
      maxBrands: criteria.maxBrands ? parseInt(criteria.maxBrands) : undefined,
      country: criteria.country || undefined,
      excludeFromTargetLedger: criteria.excludeFromTargetLedger,
      // New filters - Fixed data transformation logic
      queryable_hasFBA: criteria.queryable_hasFBA && criteria.queryable_hasFBA !== '' ?
        (criteria.queryable_hasFBA === 'true' || criteria.queryable_hasFBA === 'fba_only' ? true :
         criteria.queryable_hasFBA === 'false' || criteria.queryable_hasFBA === 'non_fba_only' ? false : undefined) : undefined,
      minRating: criteria.minRating && criteria.minRating !== '' ? parseFloat(criteria.minRating) : undefined,
      maxRating: criteria.maxRating && criteria.maxRating !== '' ? parseFloat(criteria.maxRating) : undefined,
      excludeShipsFromChina: criteria.excludeSellersFromChina, // Boolean value, no conversion needed
      minAvgBuyBoxCompetitors: criteria.minAvgBuyBoxCompetitors && criteria.minAvgBuyBoxCompetitors !== '' ? parseFloat(criteria.minAvgBuyBoxCompetitors) : undefined,
      maxAvgBuyBoxCompetitors: criteria.maxAvgBuyBoxCompetitors && criteria.maxAvgBuyBoxCompetitors !== '' ? parseFloat(criteria.maxAvgBuyBoxCompetitors) : undefined,
      minBuyBoxNewOwnershipRate: criteria.minBuyBoxNewOwnershipRate && criteria.minBuyBoxNewOwnershipRate !== '' ? parseInt(criteria.minBuyBoxNewOwnershipRate) : undefined,
      maxBuyBoxNewOwnershipRate: criteria.maxBuyBoxNewOwnershipRate && criteria.maxBuyBoxNewOwnershipRate !== '' ? parseInt(criteria.maxBuyBoxNewOwnershipRate) : undefined,
      minBuyBoxUsedOwnershipRate: criteria.minBuyBoxUsedOwnershipRate && criteria.minBuyBoxUsedOwnershipRate !== '' ? parseInt(criteria.minBuyBoxUsedOwnershipRate) : undefined,
      maxBuyBoxUsedOwnershipRate: criteria.maxBuyBoxUsedOwnershipRate && criteria.maxBuyBoxUsedOwnershipRate !== '' ? parseInt(criteria.maxBuyBoxUsedOwnershipRate) : undefined,
      countOnly,
      lastVisible: cursor, // Keep for backward compatibility
      offset: cursor ? parseInt(cursor) || undefined : undefined, // Convert cursor to offset
    };

    try {
      // Get Firebase Functions instance
      const functions = getFunctions();
      
      // Create callable function reference
      const getSellersByFilter = httpsCallable(functions, 'getSellersByFilter');
      
      console.log('🚀 Calling Firebase Function with data:', requestData);
      console.log('🔍 New filters debug - Frontend to Backend mapping:', {
        'Frontend excludeSellersFromChina': criteria.excludeSellersFromChina,
        'Backend excludeShipsFromChina': requestData.excludeShipsFromChina,
        'Frontend queryable_hasFBA': criteria.queryable_hasFBA,
        'Backend queryable_hasFBA': requestData.queryable_hasFBA,
        'Frontend minRating': criteria.minRating,
        'Backend minRating': requestData.minRating,
        'Frontend minAvgBuyBoxCompetitors': criteria.minAvgBuyBoxCompetitors,
        'Backend minAvgBuyBoxCompetitors': requestData.minAvgBuyBoxCompetitors,
        'Frontend minBuyBoxNewOwnershipRate': criteria.minBuyBoxNewOwnershipRate,
        'Backend minBuyBoxNewOwnershipRate': requestData.minBuyBoxNewOwnershipRate,
        'Frontend minBuyBoxUsedOwnershipRate': criteria.minBuyBoxUsedOwnershipRate,
        'Backend minBuyBoxUsedOwnershipRate': requestData.minBuyBoxUsedOwnershipRate
      });
      
      // Call the Firebase Function
      const result = await getSellersByFilter(requestData);
      
      console.log('✅ Firebase Function response received:', result.data);
      
      const responseData = result.data as FilterResponse;
      
      // Transform the response to include id field
      if (responseData.sellers) {
        responseData.sellers = responseData.sellers.map((seller: any) => ({
          ...seller,
          id: seller.id || seller.sellerId // Use existing id or fallback to sellerId
        }));
      }

      return responseData;
    } catch (error: any) {
      console.error('❌ Firebase Function call failed:', error);
      console.error('❌ Error details:', {
        code: error.code,
        message: error.message,
        details: error.details,
        fullError: error
      });
      
      // Handle Firebase Functions specific errors
      if (error.code) {
        switch (error.code) {
          case 'functions/deadline-exceeded':
            throw new Error('Request timed out. Please try again or use more specific filters.');
          case 'functions/unauthenticated':
            throw new Error('Authentication failed. Please sign in again.');
          case 'functions/permission-denied':
            throw new Error('Permission denied. Please check your access rights.');
          case 'functions/unavailable':
            throw new Error('Service temporarily unavailable. Please try again later.');
          case 'functions/failed-precondition':
            // This is the new error code for index errors
            console.log('🎯 Found failed-precondition error (likely index):', error.message);
            throw new Error(error.message || 'Database precondition failed');
          case 'functions/resource-exhausted':
            throw new Error('Database quota exceeded. Please try again later.');
          case 'functions/internal':
            // For internal errors, check multiple places for index information
            const errorMessage = error.message || '';
            const errorDetails = error.details || '';
            const fullErrorString = JSON.stringify(error);
            
            console.log('🔍 Checking for index info in:', {
              message: errorMessage,
              details: errorDetails,
              fullError: fullErrorString
            });
            
            if (errorMessage.includes('index') || errorDetails.includes('index') || fullErrorString.includes('index')) {
              // Try to extract the most detailed message
              const detailedMessage = errorDetails || errorMessage || 'Index error detected';
              console.log('🎯 Found index error, using message:', detailedMessage);
              throw new Error(detailedMessage);
            }
            throw new Error('Internal server error. Please try again or contact support.');
          default:
            throw new Error(error.message || 'Failed to fetch sellers');
        }
      }
      
      // Handle other errors - also check for index information
      if (error instanceof Error) {
        if (error.message.includes('index')) {
          console.log('🎯 Found index error in Error object:', error.message);
          throw error;
        }
        throw error;
      }
      
      throw new Error('Failed to fetch sellers');
    }
  };

  // Removed separate count update - now using single data query for both count and data
  // This eliminates race conditions and improves performance

  // Helper function to remove duplicates based on ID
  const removeDuplicateSellers = (existingSellers: SellerWithId[], newSellers: SellerWithId[]): SellerWithId[] => {
    const existingIds = new Set(existingSellers.map(seller => seller.id));
    const uniqueNewSellers = newSellers.filter(seller => !existingIds.has(seller.id));
    return [...existingSellers, ...uniqueNewSellers];
  };

  // Load sellers data with specific filters
  const loadSellersWithFilters = async (filtersToUse: CohortFilterCriteria, loadMore: boolean = false) => {
    // Prevent multiple simultaneous calls
    if (isLoadingRef.current) {
      console.log(`🚫 Skipping API call - already loading`);
      return;
    }

    try {
      isLoadingRef.current = true;
      
      if (loadMore) {
        setLoadingMore(true);
      } else {
        setSellersLoading(true);
        setCountLoading(true); // Set count loading for initial loads
        setAllSellers([]);
        setLastVisible(undefined);
        setCurrentOffset(0);
      }
      
      setError(null);
      
      // Get current offset directly from state
      const offset = loadMore ? currentOffset : 0;
      
      console.log(`🚀 Making API call - loadMore: ${loadMore}, offset: ${offset}, currentOffset: ${currentOffset}`);
      
      const result = await callFilterAPI(
        filtersToUse,
        false,
        loadMore ? offset.toString() : undefined // Only send offset for pagination requests
      );
      
      if (loadMore) {
        // Prevent duplicates when loading more data
        setAllSellers(prev => {
          const updated = removeDuplicateSellers(prev, result.sellers);
          console.log(`🔄 Load More: Previous ${prev.length}, New ${result.sellers.length}, Total ${updated.length}`);
          setHasMore(updated.length < totalCount);
          return updated;
        });
        setCurrentOffset(prev => {
          const newOffset = prev + result.sellers.length;
          console.log(`🔄 Updated offset from ${prev} to ${newOffset}`);
          return newOffset;
        });
        // CRITICAL: Do NOT update totalCount on pagination requests
        console.log(`🔄 Load More: Keeping existing total count: ${totalCount}`);
      } else {
        setAllSellers(result.sellers);
        setCurrentOffset(result.sellers.length);
        // ONLY update total count on initial load (when filters change)
        if (result.totalCount > 0) {
          setTotalCount(result.totalCount);
          console.log(`🎯 Initial Load: Set total count to ${result.totalCount}, loaded ${result.sellers.length} sellers`);
        } else {
          console.warn(`🎯 Initial Load: Backend returned totalCount = 0, keeping existing count: ${totalCount}`);
        }
        // Set hasMore for initial load
        setHasMore(result.sellers.length < (result.totalCount || totalCount));
        console.log(`🎯 Filter criteria:`, JSON.stringify(filtersToUse, null, 2));
      }
      
      setLastVisible(result.lastVisible);
      
    } catch (err) {
      console.error("Error fetching sellers:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch sellers");
    } finally {
      isLoadingRef.current = false;
      setSellersLoading(false);
      setLoadingMore(false);
      setCountLoading(false); // Clear count loading state
    }
  };

  // Use ref to store current filters to avoid dependency issues
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  // Handle filter changes - NO auto-search, only update state
  const handleFilterChange = (key: keyof CohortFilterCriteria, value: string | boolean) => {
    const newFilters = { ...filtersRef.current, [key]: value };
    setFilters(newFilters);
    
    console.log(`🔄 Filter changed: ${key} = ${value} (no auto-search)`);
    // REMOVED: Auto-search logic - user must click "Get Seller Count" button
  };

  // Search trigger with specific filters
  const handleSearchTriggerWithFilters = (filtersToUse: CohortFilterCriteria) => {
    // Set loading state immediately
    setCountLoading(true);
    
    // Reset state before loading new data
    setTotalCount(0);
    setAllSellers([]);
    setReviewSellers([]);
    setDeletedSellerIds(new Set());
    setLastVisible(undefined);
    setCurrentOffset(0);
    setHasMore(false);
    setCurrentView('filters'); // Reset to filters view
    
    console.log(`🔍 Search triggered with specific filters:`, filtersToUse);
    
    // Load sellers with specified filters
    loadSellersWithFilters(filtersToUse, false);
  };

  // Manual search trigger function (uses current filters)
  const handleSearchTrigger = () => {
    handleSearchTriggerWithFilters(filters);
  };

  // Load sellers data (for refresh) - only used in filter view now
  const loadSellers = async (loadMore: boolean = false) => {
    return loadSellersWithFilters(filters, loadMore);
  };

  // Handle Create Cohort button click - with 5000 seller limit enforcement
  const handleCreateCohort = async () => {
    // Hard limit check - this should never happen due to UI restrictions, but safety first
    if (totalCount > 5000) {
      alert(
        `Cannot create cohort: ${totalCount.toLocaleString()} sellers exceeds the maximum limit of 5,000 sellers.\n\n` +
        `Please refine your filters to reduce the cohort size to 5,000 or fewer sellers.`
      );
      return;
    }

    if (totalCount === 0) {
      console.warn('⚠️ No sellers available for cohort creation');
      return;
    }

    console.log(`🚀 Creating cohort with ${totalCount} total sellers (currently loaded: ${allSellers.length})`);
    
    try {
      setSellersLoading(true);
      
      // If we have fewer loaded sellers than total count, we need to load all sellers
      if (allSellers.length < totalCount) {
        console.log(`📥 Loading all ${totalCount} sellers for cohort creation...`);
        
        // Load all sellers by making multiple API calls if necessary
        const allLoadedSellers = await loadAllSellers();
        
        console.log(`✅ Loaded ${allLoadedSellers.length} sellers for cohort`);
        
        // Initialize review sellers with ALL sellers
        setReviewSellers([...allLoadedSellers]);
        setDeletedSellerIds(new Set());
        
        // Update allSellers state with complete data
        setAllSellers(allLoadedSellers);
        
      } else {
        // We already have all sellers loaded
        setReviewSellers([...allSellers]);
        setDeletedSellerIds(new Set());
      }
      
      // Navigate to review page
      setCurrentView('review');
      
    } catch (error) {
      console.error('❌ Failed to load all sellers:', error);
      
      // Show user-friendly error message
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(
        `Failed to load all sellers for the cohort due to a network or server error.\n\n` +
        `Error: ${errorMessage}\n\n` +
        `Please try again. If the problem persists, try using more specific filters to reduce the cohort size.`
      );
      
    } finally {
      setSellersLoading(false);
    }
  };

  // Helper function to load all sellers for cohort creation (up to 5000 max)
  const loadAllSellers = async (): Promise<SellerWithId[]> => {
    const allLoadedSellers: SellerWithId[] = [];
    let currentOffset = 0;
    let hasMoreData = true;
    const batchSize = 500; // Optimized batch size for performance
    const maxRetries = 3;
    
    console.log(`📥 Starting to load all ${totalCount} sellers for cohort creation...`);
    
    while (hasMoreData && allLoadedSellers.length < totalCount) {
      let retryCount = 0;
      let batchLoaded = false;
      
      while (retryCount < maxRetries && !batchLoaded) {
        try {
          console.log(`📥 Loading batch starting at offset ${currentOffset} (attempt ${retryCount + 1}/${maxRetries})...`);
          
          const result = await callFilterAPI(
            filters,
            false,
            currentOffset.toString()
          );
          
          if (result.sellers && result.sellers.length > 0) {
            // Add new sellers, avoiding duplicates
            const newSellers = result.sellers.filter(seller =>
              !allLoadedSellers.some(existing => existing.id === seller.id)
            );
            allLoadedSellers.push(...newSellers);
            
            console.log(`📥 Loaded batch: ${result.sellers.length} sellers, total so far: ${allLoadedSellers.length}/${totalCount}`);
            
            // Check if we have more data
            hasMoreData = result.sellers.length === batchSize && allLoadedSellers.length < totalCount;
            currentOffset += result.sellers.length;
            batchLoaded = true;
          } else {
            console.log(`📥 No more sellers returned, stopping load`);
            hasMoreData = false;
            batchLoaded = true;
          }
          
        } catch (error) {
          retryCount++;
          console.warn(`⚠️ Batch load failed (attempt ${retryCount}/${maxRetries}):`, error);
          
          if (retryCount >= maxRetries) {
            console.error(`❌ Failed to load batch after ${maxRetries} attempts`);
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            throw new Error(`Failed to load sellers after ${maxRetries} attempts: ${errorMessage}`);
          } else {
            // Wait before retry (exponential backoff)
            const waitTime = Math.pow(2, retryCount) * 1000;
            console.log(`⏳ Waiting ${waitTime}ms before retry...`);
            await new Promise(resolve => setTimeout(resolve, waitTime));
          }
        }
      }
      
      // Safety check to prevent infinite loops
      if (currentOffset > totalCount * 2) {
        console.warn('⚠️ Safety break: offset exceeded expected total count');
        break;
      }
    }
    
    console.log(`✅ Finished loading sellers. Loaded: ${allLoadedSellers.length}/${totalCount} total`);
    return allLoadedSellers;
  };

  // Handle back to filters navigation
  const handleBackToFilters = () => {
    setCurrentView('filters');
  };

  // Handle seller deletion in review page
  const handleSellerDelete = (sellerId: string) => {
    console.log(`🗑️ Removing seller ${sellerId} from cohort`);
    
    // Add to deleted set
    setDeletedSellerIds(prev => new Set([...prev, sellerId]));
    
    // Remove from review sellers
    setReviewSellers(prev => prev.filter(seller => seller.id !== sellerId));
  };

  // Handle address validation from review page - now navigates to validation page
  const handleAddressValidate = async () => {
    if (reviewSellers.length === 0) {
      console.warn('⚠️ No sellers available for address validation');
      return;
    }

    // Prevent multiple simultaneous validations
    if (isValidationInProgress) {
      console.warn('⚠️ Address validation already in progress');
      return;
    }

    console.log(`🚀 Starting address validation for ${reviewSellers.length} sellers`);
    
    setIsValidationInProgress(true);
    setCurrentView('validation');
    
    try {
      // Prepare seller IDs for validation
      const sellerIds = reviewSellers.map(seller => seller.id);

      console.log(`📍 Starting validation for ${sellerIds.length} sellers`);

      // Call the address validation service (which calls Firebase function directly)
      const validationData = await addressValidationService.validateAddresses(sellerIds);
      
      if (!validationData.success) {
        throw new Error('Address validation failed');
      }

      console.log(`✅ Address validation completed:`, validationData.summary);
      
      // Set the validation results
      setValidationResults(validationData.results || []);
      
    } catch (error) {
      console.error('❌ Address validation failed:', error);
      // Create mock results to show the error
      const errorResults = reviewSellers.map(seller => ({
        sellerId: seller.sellerId,
        sellerName: seller.sellerData?.sellerName || seller.queryable_sellerName || 'Unknown',
        status: 'ERROR' as const,
        statusReason: error instanceof Error ? error.message : 'Validation failed',
        originalAddress: {
          line1: seller.sellerData?.address?.[0] || '',
          city: seller.queryable_address?.city || '',
          state: seller.queryable_address?.state || '',
          zip: seller.queryable_address?.zip || '',
          country: seller.queryable_address?.country || 'US'
        },
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        processedAt: new Date() as any // Will be converted to Timestamp
      }));
      setValidationResults(errorResults);
    } finally {
      setIsValidationInProgress(false);
    }
  };

  // Handle back from validation page
  const handleBackFromValidation = () => {
    setCurrentView('review');
    setIsValidationInProgress(false); // Reset validation in progress state
  };

  // Phase 1: Handle sending cohort for review
  const handleSendForReview = async (data: {
    creatorName: string;
    cohortNotes: string;
    cohortNumber: number;
    sellerIds: string[];
    validationResults: object[];
    filters: object;
  }) => {
    try {
      console.log('🚀 Sending cohort for review:', {
        creatorName: data.creatorName,
        cohortNumber: data.cohortNumber,
        sellerCount: data.sellerIds.length,
        validationResultsCount: data.validationResults.length
      });

      // Call the cohort review service
      const result = await cohortReviewService.startCohortReview(data);

      console.log('✅ Cohort sent for review successfully:', result);

      // Show success message
      alert(`Success! Cohort #${data.cohortNumber} has been sent to the VA team for review.\n\nCohort ID: ${result.cohortId}`);

      // Navigate back to filters view
      setCurrentView('filters');

      // Reset review state
      setReviewSellers([]);
      setDeletedSellerIds(new Set());

    } catch (error) {
      console.error('❌ Failed to send cohort for review:', error);
      
      // Show error message
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(`Failed to send cohort for review.\n\nError: ${errorMessage}\n\nPlease try again.`);
      
      // Re-throw the error so the CohortReviewPage can handle it
      throw error;
    }
  };

  // REMOVED: Initial auto-search on page load
  // User must manually click "Get Seller Count" button to search

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto">
      {currentView === 'filters' ? (
        <>
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Cohort Builder
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Create targeted seller cohorts for direct mail campaigns
            </p>
          </div>

          {/* Filter Form Component */}
          <CohortFilterForm
            filters={filters}
            onFilterChange={handleFilterChange}
            totalCount={totalCount}
            countLoading={sellersLoading || countLoading}
            onCreateCohort={handleCreateCohort}
            onSearchTrigger={handleSearchTrigger}
            isValidationInProgress={isValidationInProgress}
            formDisabled={sellersLoading || countLoading}
          />

          {/* Simple Index Creation Link - Development Helper */}
          {error && error.includes('index') && (
            <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <p className="text-sm text-amber-800 dark:text-amber-200 mb-2">
                This query requires an index.
                {(() => {
                  const urlMatch = error.match(/https:\/\/console\.firebase\.google\.com[^\s]+/);
                  if (urlMatch) {
                    return (
                      <>
                        {' '}
                        <a
                          href={urlMatch[0]}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-amber-700 dark:text-amber-300 underline hover:text-amber-900 dark:hover:text-amber-100"
                        >
                          Create Index
                        </a>
                      </>
                    );
                  }
                  return null;
                })()}
              </p>
            </div>
          )}

          {/* Note: Removed SellersResultTable from filter view as per requirements */}
        </>
      ) : currentView === 'review' ? (
        /* Review Page */
        <CohortReviewPage
          sellers={reviewSellers}
          totalCount={reviewSellers.length}
          filters={filters}
          onBackToFilters={handleBackToFilters}
          onSellerDelete={handleSellerDelete}
          onSendForReview={handleSendForReview}
        />
      ) : (
        /* Address Validation Page */
        <AddressValidationPage
          validationResults={validationResults}
          onBack={handleBackFromValidation}
          isLoading={isValidationInProgress}
        />
      )}
    </div>
  );
};

export default PpcFarmCohortBuilderClient;